import { useCallback, useMemo } from "react";
import { useCanvasStore, type AwaitingInput } from "@/stores/canvas-store";
import { useFrameField } from "@/stores/frames-selectors";
import { useCompanionStore } from "@/stores/companion-store";
import { translate } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";

export type FrameAttention =
  | {
      kind: "question";
      awaitingInputId: string;
      question: string;
      pendingCount: number;
    }
  | { kind: "error"; message: string | null }
  | null;

const NO_QUESTIONS: AwaitingInput[] = [];
/** Frames that report failures in their own body (spec §5.4, §8). */
const SELF_REPORTING_FRAME_TYPES = new Set(["terminal"]);

function oldestQuestion(questions: AwaitingInput[]): AwaitingInput {
  return questions.reduce((oldest, candidate) =>
    Date.parse(candidate.createdAt) < Date.parse(oldest.createdAt)
      ? candidate
      : oldest,
  );
}

/** The frame's question dot (oldest pending question) or error dot. */
export function useFrameAttention(frameId: string): FrameAttention {
  const questions = useCanvasStore(
    useCallback(
      (state) => state.awaitingInputs.get(frameId) ?? NO_QUESTIONS,
      [frameId],
    ),
  );
  // "" marks an errored frame whose run is unknown; null a frame not in error.
  const errorExecutionId = useFrameField(frameId, (frame) => {
    if (!frame || frame.status !== "error") return null;
    if (SELF_REPORTING_FRAME_TYPES.has(frame.frameType)) return null;
    const executionId = frame.metadata?.execution_id;
    return typeof executionId === "string" ? executionId : "";
  });
  const errorMessage = useCompanionStore(
    useCallback(
      (state) =>
        errorExecutionId
          ? (state.executions.get(errorExecutionId)?.errorMessage ?? null)
          : null,
      [errorExecutionId],
    ),
  );
  return useMemo((): FrameAttention => {
    if (questions.length > 0) {
      const oldest = oldestQuestion(questions);
      return {
        kind: "question",
        awaitingInputId: oldest.id,
        question: oldest.question,
        pendingCount: questions.length,
      };
    }
    if (errorExecutionId === null) return null;
    return { kind: "error", message: errorMessage };
  }, [errorExecutionId, errorMessage, questions]);
}

/** What the dot's label and the title tooltip say (spec §5.4). */
export function frameAttentionText(attention: FrameAttention): string | null {
  if (!attention) return null;
  if (attention.kind === "question") return attention.question;
  return (
    attention.message ?? translate(framesSharedMessages, "frameDidNotFinish")
  );
}
